/** Cursor required to load page N (scroll API: `lastSelectedId` from page N − 1). */
export type ScrollPageUrlState = {
  page: number;
  lastSelectedId?: number;
};

/** Notifications `LastSelectedId` is a long. GUIDs from notification ids 400. */
export function parseNumericLastSelectedId(
  value: unknown,
): number | undefined {
  if (value == null || value === "") return undefined;
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : undefined;
  }
  const raw = String(value).trim();
  if (!/^-?\d+$/.test(raw)) return undefined;
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) ? parsed : undefined;
}

export function parseScrollPageParams(searchParams: URLSearchParams): ScrollPageUrlState {
  const pageRaw = searchParams.get("page");
  const parsedPage = pageRaw != null ? Number.parseInt(pageRaw, 10) : 1;
  const page = Number.isFinite(parsedPage) && parsedPage >= 1 ? parsedPage : 1;

  if (page <= 1) {
    return { page: 1 };
  }

  const cursorRaw = searchParams.get("lastSelectedId");
  const lastSelectedId =
    cursorRaw != null ? Number.parseInt(cursorRaw, 10) : Number.NaN;
  if (!Number.isFinite(lastSelectedId)) {
    return { page: 1 };
  }

  return { page, lastSelectedId };
}

export function normalizePathnameQuery(query: string): string {
  if (!query) return "";
  return query.startsWith("?") ? query : `?${query}`;
}

export function searchParamsToQuery(searchParams: URLSearchParams): string {
  const encoded = searchParams.toString();
  return encoded ? `?${encoded}` : "";
}

/** Avoid `router.replace` when the URL already matches — that retrigger looped searchParams effects. */
export function replacePathnameQueryIfChanged(
  router: { replace: (href: string, options?: { scroll?: boolean }) => void },
  pathname: string,
  searchParams: URLSearchParams,
  query: string,
): boolean {
  const next = normalizePathnameQuery(query);
  if (next === searchParamsToQuery(searchParams)) {
    return false;
  }
  router.replace(next ? `${pathname}${next}` : pathname, { scroll: false });
  return true;
}

export function buildScrollPageQuery(page: number, lastSelectedId?: number): string {
  if (page <= 1) {
    return "";
  }
  const params = new URLSearchParams();
  params.set("page", String(page));
  if (lastSelectedId != null && Number.isFinite(lastSelectedId)) {
    params.set("lastSelectedId", String(lastSelectedId));
  }
  return `?${params.toString()}`;
}

export function scrollPageUrlMatchesState(
  searchParams: URLSearchParams,
  page: number,
  cursorAfterPage: Record<number, number>,
): boolean {
  const fromUrl = parseScrollPageParams(searchParams);
  if (fromUrl.page !== page) return false;
  if (page <= 1) return true;
  const cursorForPage = cursorAfterPage[page - 1];
  return fromUrl.lastSelectedId === cursorForPage;
}

/**
 * Skip state→URL sync until state can represent the target page.
 * Allows Previous (state &lt; URL) when cursors for the target page exist;
 * still blocks hydration from wiping the URL while stuck on page 1.
 */
export function shouldDeferScrollPageUrlSync(
  fromUrl: ScrollPageUrlState,
  currentPage: number,
  cursorAfterPage: Record<number, number>,
): boolean {
  if (fromUrl.page > currentPage && cursorAfterPage[currentPage] == null) {
    return true;
  }
  if (
    fromUrl.page > 1 &&
    fromUrl.lastSelectedId != null &&
    cursorAfterPage[fromUrl.page - 1] !== fromUrl.lastSelectedId
  ) {
    return true;
  }
  if (currentPage > 1 && cursorAfterPage[currentPage - 1] == null) {
    return true;
  }
  return false;
}

export function scrollPageParamsFromRecord(params: {
  page?: string;
  lastSelectedId?: string;
}): ScrollPageUrlState {
  const urlParams = new URLSearchParams();
  if (params.page) urlParams.set("page", params.page);
  if (params.lastSelectedId) urlParams.set("lastSelectedId", params.lastSelectedId);
  return parseScrollPageParams(urlParams);
}

/** Total rows from a scroll/search page response; falls back to loaded item count. */
export function paginatedTotalCount(
  data: { totalCount?: unknown; items?: unknown[] } | undefined | null,
  fallbackLength = 0,
): number {
  const raw = data?.totalCount;
  if (raw != null) {
    const parsed = typeof raw === "number" ? raw : Number(raw);
    if (Number.isFinite(parsed) && parsed >= 0) {
      return parsed;
    }
  }
  return data?.items?.length ?? fallbackLength;
}
